/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderFormSchemaFactory, FormRootSchema, FormStateSchema, whenUsRegion } from '@gh/builder-shared';
import { BuilderPermission, ContactTermsReceivables } from '@gh/builder-shared-data';
import {
  FormState,
  MaskedTypes,
  NumberTypes,
  toAppFormGroup,
  updateSchemaWithChildren,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { FormMode, getControlValue, markControlAsDirty, setControlValue } from '@gh/core-ui';

import { ExcludedCcRange, isSystemVarsDayControlsByContactTermsEnabled, SystemVarsReceivables } from '../../resources';

const isBalanceRequired = (paymentDue: ContactTermsReceivables) => {
  switch (paymentDue) {
    case ContactTermsReceivables.InDays:
    case ContactTermsReceivables.OnMonthDay:
    case ContactTermsReceivables.DaysAfterEOM:
    case ContactTermsReceivables.MonthDayAfterEOM:
      return true;
    default:
      return false;
  }
};

const isDayOfMonthType = (paymentDue: ContactTermsReceivables) => {
  switch (paymentDue) {
    case ContactTermsReceivables.OnMonthDay:
    case ContactTermsReceivables.MonthDayAfterEOM:
      return true;
    default:
      return false;
  }
};

@Injectable()
export class SystemVarsReceivablesFormState extends FormState<SystemVarsReceivables> {

  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: UserContext) {
    super(schemaFactory);
  }

  get excludedCostCenters(): ExcludedCcRange[] {
    return getControlValue(this.group, 'excludedCostCenters');
  }

  disableOrEnableControlsByPaymentDue(paymentDue: ContactTermsReceivables): void {
    updateSchemaWithChildren(toAppFormGroup(this.group), {
      children: {
        recDefaultPaymentBalanceDays: {
          required: isBalanceRequired(paymentDue),
          type: isDayOfMonthType(paymentDue) ? NumberTypes.DayOfMonth : NumberTypes.Days,
          enabled: isSystemVarsDayControlsByContactTermsEnabled(paymentDue),
        },
        recDefaultPaymentDiscountDays: {
          enabled: isSystemVarsDayControlsByContactTermsEnabled(paymentDue),
        },
      },
    });
  }

  setExcludedCostCenters(excludedCostCenters: ExcludedCcRange[]): void {
    setControlValue(this.group, 'excludedCostCenters', excludedCostCenters);
    markControlAsDirty(this.group, 'excludedCostCenters');
  }

  protected buildSchema(receivables: SystemVarsReceivables, { root }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasPermission(BuilderPermission.Setup.SystemVariables.Receivables.EDIT)
      ? FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        recDefaultPaymentDue: { required: true },
        recDefaultPaymentBalanceDays: {
          required: isBalanceRequired(receivables.recDefaultPaymentDue),
          type: isDayOfMonthType(receivables.recDefaultPaymentDue) ? NumberTypes.DayOfMonth : NumberTypes.Days,
          enabled: isSystemVarsDayControlsByContactTermsEnabled(receivables.recDefaultPaymentDue),
        },
        recDefaultPaymentDiscountDays: {
          type: NumberTypes.Days,
          enabled: isSystemVarsDayControlsByContactTermsEnabled(receivables.recDefaultPaymentDue),
        },
        recDefaultPaymentDiscountPct: { type: NumberTypes.Percents_2 },
        latePaymentTerms: void 0,
        showBankDetails: void 0,
        drawLetterRequired: void 0,
        drawStatementRequired: void 0,
        escrowAllowed: { visible: whenUsRegion() },
        lenderScheduleAllowed: void 0,
        excludedCostCenters: void 0,
        bsbNumber: { type: MaskedTypes.BsbNumber },
        bankName: void 0,
        bankBranchName: void 0,
        accountNumber: void 0,
        accountName: void 0,
      },
    });
  }
}
